// Gallery thumbnails: a cache, deliberately, and never a record.
//
// The canonical gallery is coordinates only (see gallery.js) — that is what makes
// D7 free and what Block I migrates into D1. Thumbnails are base64 PNGs, which are
// orders of magnitude larger, so they live in their own key that can be evicted,
// corrupted, or wiped at any moment with no loss: anything missing is re-rendered
// from the bounds. Nothing may ever be stored ONLY here.
//
// The reason to cache at all is the render-cost trap: renderRegion is expensive
// and synchronous on the main thread, so an uncached gallery re-renders every
// thumbnail on every visit. Caching makes the second visit instant.
//
// Note a thumbnail is NOT a shrunk copy of the full render, and that is correct
// behaviour, not a bug. The CAUSE changed with the fixed colour map (colourMap.js)
// and the conclusion did not. It used to be colour: equalisation ranked each pixel
// within the sampled set, so a coarser grid landed on different colours. A
// thumbnail and the full render now share one region-derived map, so that cause is
// gone — and measurement says it was never the big one. What remains is spatial:
// a thumbnail averages four subpixels where the full render averages a hundred
// over the same area, so it cannot resolve filigree it has not sampled. That is
// aliasing, it is irreducible by any colour scheme, and it is the whole of the
// difference now. The full-screen render is still the truth; the thumbnail is an
// approximation of it. (Neither touches claim determinism, which is defined at a
// fixed render size.)

import generate from './MandGen';
import { boundsAspect, thumbDims } from './renderSize';

export const THUMBS_KEY = 'fractalart.thumbs.v1';
const SCHEMA_VERSION = 1;

// Enough to cover a browsing session's worth of scrolling without approaching the
// ~5MB origin quota (each thumb is tens of KB). Beyond this, oldest goes first.
const MAX_THUMBS = 32;

const emptyCache = () => ({ version: SCHEMA_VERSION, thumbs: {} });

const loadCache = () => {
    let raw;
    try {
        raw = window.localStorage.getItem(THUMBS_KEY);
    } catch {
        return emptyCache();
    }
    if (!raw) return emptyCache();
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.thumbs !== 'object' || parsed.thumbs === null) {
            return emptyCache();
        }
        return { version: SCHEMA_VERSION, thumbs: parsed.thumbs };
    } catch {
        return emptyCache();
    }
};

// Oldest-written first, for eviction.
const idsByAge = (thumbs) =>
    Object.keys(thumbs).sort((a, b) => (thumbs[a]?.at || 0) - (thumbs[b]?.at || 0));

// A failed write is not an error worth surfacing: the cache is optional, and the
// image is always recoverable from the coordinates. Retry once after dropping the
// oldest half, then give up quietly.
const writeCache = (cache) => {
    try {
        window.localStorage.setItem(THUMBS_KEY, JSON.stringify(cache));
        return true;
    } catch {
        const ordered = idsByAge(cache.thumbs);
        const drop = ordered.slice(0, Math.ceil(ordered.length / 2));
        if (drop.length === 0) return false;
        drop.forEach(id => { delete cache.thumbs[id]; });
        try {
            window.localStorage.setItem(THUMBS_KEY, JSON.stringify(cache));
            return true;
        } catch {
            return false;
        }
    }
};

const dataUrlOf = (record) =>
    record && typeof record.dataUrl === 'string' ? record.dataUrl : null;

export const getThumb = (id) => dataUrlOf(loadCache().thumbs[id]);

// The whole cache as a plain { id: dataUrl } map, in ONE read.
//
// Worth having as its own call: the cache holds up to MAX_THUMBS base64 PNGs, so
// it is on the order of a megabyte of JSON. Calling getThumb once per entry to
// build this map would parse that megabyte once per entry — which is how a cache
// meant to make the gallery fast ends up being the slowest thing on the page.
export const getAllThumbs = () => {
    const { thumbs } = loadCache();
    const map = {};
    Object.keys(thumbs).forEach(id => {
        const dataUrl = dataUrlOf(thumbs[id]);
        if (dataUrl) map[id] = dataUrl;
    });
    return map;
};

export const putThumb = (id, dataUrl) => {
    const cache = loadCache();
    cache.thumbs[id] = { dataUrl, at: Date.now() };

    const ordered = idsByAge(cache.thumbs);
    if (ordered.length > MAX_THUMBS) {
        ordered.slice(0, ordered.length - MAX_THUMBS).forEach(key => {
            delete cache.thumbs[key];
        });
    }
    return writeCache(cache);
};

// Drop cached images for entries that no longer exist, so removing a specimen
// actually frees its bytes instead of waiting for eviction.
export const pruneThumbs = (liveIds) => {
    const keep = new Set(liveIds);
    const cache = loadCache();
    const stale = Object.keys(cache.thumbs).filter(id => !keep.has(id));
    if (stale.length === 0) return;
    stale.forEach(id => { delete cache.thumbs[id]; });
    writeCache(cache);
};

// Render a small thumbnail for an entry and cache it. Synchronous and CPU-bound
// (~14k pixels — cheap, but not free), so callers must yield to the event loop
// between entries rather than looping over a whole gallery in one tick.
export const renderThumb = (entry) => {
    const { width, height } = thumbDims(boundsAspect(entry));
    const rendered = generate({
        manual: true,
        width,
        height,
        axisValues: {
            newMinR: entry.realMin,
            newMaxR: entry.realMax,
            newMinI: entry.imaginaryMin,
            newMaxI: entry.imaginaryMax
        }
    });
    putThumb(rendered.id, rendered.dataUrl);
    return rendered.dataUrl;
};

// Cache-first: the whole point of the cache is that this returns without rendering
// on a return visit.
export const thumbFor = (entry) => getThumb(entry.id) || renderThumb(entry);
